import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  Select,
  DatePicker,
  Radio,
  Button,
  notification,
} from "antd";
import { PatientsDTO } from "../../models/Patients";
import { patientsService } from "../../services/patientsService";
import { casesService } from "../../services/casesService";
import { useAuth } from "../../contexts/authContext";
import { CasesDTO } from "../../models/Cases";
import { useLoaderProvider } from "../../contexts/LoaderContext";
import { provinces } from "../../config/constants";
import Title from "antd/es/typography/Title";
import moment from "moment";
import "./AnagraficaPaziente.css";

const { Option } = Select;

interface AnagraficaPazienteProps {
  patient?: PatientsDTO | null;
  isEditable: boolean;
  onPatientCreated?: (patient: PatientsDTO, caseId: number) => void;
  onPatientUpdated: (patientId: number) => void;
  onCaseSaved?: (caseSaved: CasesDTO) => void;
}

const AnagraficaPaziente: React.FC<AnagraficaPazienteProps> = ({
  patient,
  isEditable,
  onPatientCreated,
  onPatientUpdated,
  onCaseSaved,
}) => {
  const { showLoader, hideLoader } = useLoaderProvider();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEditMode = !!patient;
  const { loggedDoctor } = useAuth();

  const formValues = Form.useWatch([], form);

  useEffect(() => {
    if (patient) {
      form.setFieldsValue({
        ...patient,
        birthDate: patient.birthDate ? moment(patient.birthDate) : undefined,
      });
    }
  }, [patient, form]);

  const onFinish = async (values: PatientsDTO) => {
    if (!isEditable) return;

    setLoading(true);
    showLoader();
    try {
      if (isEditMode) {
        values.id = patient!.id;
        const updatedPatient = await patientsService.updatePatient(
          patient!.id!,
          {
            ...values,
            anonymizedId: patient.anonymizedId,
            privacyUploadDate: patient.privacyUploadDate,
          }
        );
        notification.success({
          message: "Anagrafica paziente aggiornata con successo",
        });
        onPatientUpdated(updatedPatient.id!);
      } else {
        const newPatient = await patientsService.createPatient(values);
        const newCase = await casesService.createCase({
          patient: newPatient,
          doctors: [loggedDoctor!],
          caseStatus: "DRAFT",
          createdAt: new Date().toISOString(),
          createdBy: loggedDoctor!,
        });
        notification.success({
          message: "Anagrafica paziente creata con successo",
          description: "La bozza del caso è stata creata",
        });
        onPatientCreated && onPatientCreated(newPatient, newCase.id!);
        onCaseSaved && onCaseSaved(newCase);
      }
    } catch (error) {
      console.error("Errore durante il salvataggio:", error);
      notification.error({
        message: "Si è verificato un errore durante il salvataggio",
      });
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const renderFormItem = (
    name: string,
    label: string,
    rules: any[],
    component: React.ReactNode
  ) => {
    const value = formValues ? formValues[name] : undefined;

    return (
      <Form.Item
        name={name}
        label={label}
        rules={isEditable ? rules : undefined}
      >
        {isEditable ? (
          component
        ) : (
          <Input readOnly value={value || ""} className="read-only-input" />
        )}
      </Form.Item>
    );
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Title level={4}>Paziente</Title>
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem(
            "lastName",
            "Cognome",
            [{ required: true }],
            <Input />
          )}
        </Col>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem("firstName", "Nome", [{ required: true }], <Input />)}
        </Col>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem(
            "taxIdCode",
            "Codice Fiscale",
            [{ required: true }],
            <Input />
          )}
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem(
            "birthDate",
            "Data di Nascita",
            [{ required: true }],
            <DatePicker style={{ width: "100%" }} />
          )}
        </Col>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem(
            "citizenship",
            "Luogo di Nascita",
            [{ required: true }],
            <Input />
          )}
        </Col>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem(
            "gender",
            "Sesso",
            [{ required: true }],
            <Radio.Group>
              <Radio value="M">M</Radio>
              <Radio value="F">F</Radio>
            </Radio.Group>
          )}
        </Col>
      </Row>

      <Title level={4}>Recapiti</Title>
      <Row gutter={16}>
        <Col xs={24} sm={24} md={12}>
          {renderFormItem(
            "street",
            "Indirizzo",
            [{ required: true }],
            <Input />
          )}
        </Col>
        <Col xs={24} sm={12} md={4}>
          {renderFormItem("postalCode", "CAP", [{ required: true }], <Input />)}
        </Col>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem("city", "Città", [{ required: true }], <Input />)}
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem(
            "province",
            "Provincia",
            [{ required: true }],
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Seleziona provincia"
            >
              {provinces.map((province) => (
                <Option
                  key={province.value}
                  value={province.value}
                  label={province.label}
                >
                  {province.label}
                </Option>
              ))}
            </Select>
          )}
        </Col>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem("phone", "Telefono", [{ required: true }], <Input />)}
        </Col>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem("email", "Email", [{ type: "email" }], <Input />)}
        </Col>
      </Row>

      <Title level={4}>Altre informazioni</Title>
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem(
            "maritalStatus",
            "Stato Civile",
            [],
            <Select placeholder="Seleziona stato civile">
              <Option value="CELIBE">Celibe</Option>
              <Option value="NUBILE">Nubile</Option>
              <Option value="DIVORZIATO">Divorziato</Option>
              <Option value="CONIUGATO">Coniugato</Option>
              <Option value="VEDOVO">Vedovo</Option>
            </Select>
          )}
        </Col>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem("job", "Occupazione", [], <Input />)}
        </Col>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem("nationality", "Nazionalità", [], <Input />)}
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem("aslAssigned", "ASL di Appartenenza", [], <Input />)}
        </Col>
        <Col xs={24} sm={12} md={8}>
          {renderFormItem("qualification", "Qualifica", [], <Input />)}
        </Col>
      </Row>

      {isEditable && (
        <Row
          gutter={[16, 16]}
          justify="center"
          align="middle"
          style={{ marginTop: "20px", marginBottom: "20px" }}
        >
          <Col
            xs={24}
            sm={24}
            md={4}
            style={{ display: "flex", justifyContent: "center" }}
          >
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEditMode ? "Aggiorna" : "Salva"}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default AnagraficaPaziente;
